import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Chip,
  useTheme
} from '@mui/material';
import {
  Translate as TranslateIcon,
  Info as InfoIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import InlineAudioPlayer from './InlineAudioPlayer';

const MessageBubble = ({
  message,
  isUser,
  isSystem,
  onOpenWordDialog,
  onFeedback,
  getAudioUrl,
  audioReady,
  formatMessage
}) => {
  const theme = useTheme();
  const [showTranslation, setShowTranslation] = useState(false);

  // Strict check for corrections (handles empty arrays/objects)
  const hasCorrections = isUser && message.corrections &&
    (Array.isArray(message.corrections) ? message.corrections.length > 0 : !!message.corrections);

  if (isSystem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2, px: 2 }}>
        <Typography variant="caption" color="text.secondary" align="center">
          {message.content}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        width: '100%',
        px: { xs: 0, sm: 2 } // Keep original padding logic
      }}
    >
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: 2,
          maxWidth: { xs: '100%', sm: '80%' },
          bgcolor: isUser ? 'primary.light' : 'background.paper',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          position: 'relative',
          wordBreak: 'break-word'
        }}
      >
        {/* Message Content */}
        <Box>
          {formatMessage(message.content, !isUser)}
        </Box>

        {/* User Corrections - Only render if hasCorrections is true */}
        {hasCorrections && (
          <Paper sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <InfoIcon color="error" fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Correction
              </Typography>
            </Box>

            {Array.isArray(message.corrections) ? (
              message.corrections.map((correction, idx) => (
                <Box key={idx} sx={{ mb: idx < message.corrections.length - 1 ? 1.5 : 0 }}>
                  <Typography variant="body2" color="error" sx={{ textDecoration: 'line-through', fontSize: '0.9rem' }}>
                    {correction.original}
                  </Typography>
                  <Typography variant="body2" color="success.main" fontWeight="medium">
                    {correction.correction}
                  </Typography>
                  {correction.explanation && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {correction.explanation}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Box>
                <Typography variant="body2" color="error" sx={{ textDecoration: 'line-through', fontSize: '0.9rem' }}>
                  {typeof message.content === 'string' ? message.content : ''}
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight="medium">
                  {typeof message.corrections === 'string' ? message.corrections : message.corrections?.correction || ""}
                </Typography>
                {(typeof message.corrections === 'object' && message.corrections.explanation) && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    {message.corrections.explanation}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        )}

        {/* Translation Collapse */}
        {message.translation && !isUser && (
          <Collapse in={showTranslation}>
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {message.translation}
              </Typography>
            </Box>
          </Collapse>
        )}

        {/* Controls */}
        {!isUser && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 1, gap: 0.5 }}>
            <InlineAudioPlayer
              audioSrc={getAudioUrl(message.content)}
              isVisible={true}
              disabled={!audioReady}
              autoPlay={false}
            />
            {message.translation && (
              <IconButton
                size="small"
                onClick={() => setShowTranslation(!showTranslation)}
                color={showTranslation ? "primary" : "default"}
              >
                <TranslateIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}
      </Paper>

      {/* Suggestions Button */}
      {isUser && message.suggestions && (
        <Chip
          size="small"
          icon={<HelpIcon />}
          label="View Feedback"
          onClick={() => onFeedback(message)}
          sx={{ mt: 0.5, mr: 1, bgcolor: 'background.paper' }}
          variant="outlined"
        />
      )}
    </Box>
  );
};

export default MessageBubble;